import { Injectable } from '@angular/core';
import { Auth, authState, signOut } from '@angular/fire/auth';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, firstValueFrom, Observable, Subscription, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { JwtUtils, SessionStatus } from '../utils/jwt-utils';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class SessionExpirationService {
    private sessionStatusSubject = new BehaviorSubject<SessionStatus | null>(null);
    private warningThresholdHours = 2; // Show warning 2 hours before expiration
    private isMonitoring = false; // Track if monitoring is active
    private currentTimerSubscription: Subscription | null = null; // Track current timer subscription

    constructor(private auth: Auth) {
        // Wait for auth state to be ready, then start monitoring
        this.initializeWithAuthState();
    }

    /**
     * Initialize the service once auth state is available
     */
    private initializeWithAuthState(): void {
        authState(this.auth)
            .pipe(untilDestroyed(this))
            .subscribe((user) => {
                if (user) {
                    if (!this.isMonitoring) {
                        this.startSessionMonitoring();
                    }
                } else {
                    this.isMonitoring = false;
                    this.sessionStatusSubject.next(null);

                    // Clear any existing timer when user logs out
                    if (this.currentTimerSubscription) {
                        this.currentTimerSubscription.unsubscribe();
                        this.currentTimerSubscription = null;
                    }
                }
            });
    }

    /**
     * Set the warning threshold hours
     */
    setWarningThreshold(hours: number): void {
        this.warningThresholdHours = hours;
    }

    /**
     * Get the current session status as an observable
     */
    getSessionStatus$(): Observable<SessionStatus | null> {
        return this.sessionStatusSubject.asObservable();
    }

    /**
     * Check session status immediately
     */
    private async checkSessionNow(): Promise<void> {
        if (!this.auth.currentUser) {
            this.sessionStatusSubject.next(null);
            return;
        }

        try {
            const tokenWithClaims = await firstValueFrom(JwtUtils.getIdTokenWithClaims$(this.auth));

            const sessionStatus = JwtUtils.checkSessionExpiration(tokenWithClaims.claims, this.warningThresholdHours);

            // Check if session is expired (not just expiring soon)
            if (sessionStatus.hoursUntilExpiry <= 0) {
                await this.handleSessionExpired();
                return;
            }
            this.sessionStatusSubject.next(sessionStatus);

            // Schedule next check with dynamic interval
            this.scheduleNextCheck(sessionStatus.hoursUntilExpiry);
        } catch (error) {
            console.error('[SessionExpirationService] Error checking session:', error);
            this.sessionStatusSubject.next(null);
        }
    }

    private async handleSessionExpired(): Promise<void> {
        try {
            // Clear the session status first
            this.sessionStatusSubject.next(null);

            // Logout from Firebase
            await signOut(this.auth);
            window.location.reload();

            // The auth state change will trigger navigation to login
        } catch (error) {
            console.error('[SessionExpirationService] Error during automatic logout:', error);
            // Fallback: force page reload to clear session
            window.location.reload();
        }
    }

    /**
     * Calculate dynamic check interval based on time until expiration
     */
    private calculateCheckInterval(hoursUntilExpiry: number): number {
        if (hoursUntilExpiry <= 0.33) {
            // Less than 20 minutes
            return 1;
        } else if (hoursUntilExpiry <= 1) {
            // Less than 1 hour
            return 5;
        } else if (hoursUntilExpiry <= 2) {
            // Less than 2 hours
            return 10;
        } else if (hoursUntilExpiry >= 48) {
            // More than 48 hours
            return 60;
        } else {
            return 30; // Default for 2-48 hours range
        }
    }

    /**
     * Schedule the next check with dynamic interval
     */
    private scheduleNextCheck(hoursUntilExpiry: number): void {
        // Clear existing timer
        if (this.currentTimerSubscription) {
            this.currentTimerSubscription.unsubscribe();
        }

        const intervalMinutes = this.calculateCheckInterval(hoursUntilExpiry);
        const intervalMs = intervalMinutes * 60 * 1000;

        this.currentTimerSubscription = timer(intervalMs)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.checkSessionNow();
            });
    }

    /**
     * Format time until expiry for display
     */
    formatTimeUntilExpiry(hoursUntilExpiry: number): string {
        const lang = localStorage.getItem('currentLanguage') || 'en-us';
        return JwtUtils.formatTimeUntilExpiry(hoursUntilExpiry, lang);
    }

    /**
     * Start periodic session monitoring with dynamic intervals
     */
    private startSessionMonitoring(): void {
        if (this.isMonitoring) {
            return;
        }

        this.isMonitoring = true;

        // Check immediately to establish baseline and start dynamic scheduling
        this.checkSessionNow();
    }

    /**
     * Check if current session is expired
     */
    isCurrentSessionExpired(): Observable<boolean> {
        if (!this.auth.currentUser) {
            return new BehaviorSubject(false).asObservable();
        }

        return JwtUtils.getIdTokenWithClaims$(this.auth).pipe(
            map(({ claims }) => JwtUtils.isSessionExpired(claims)),
            catchError(() => new BehaviorSubject(false).asObservable()),
        );
    }

    logoutCleanup(): void {
        // Clear session status
        this.sessionStatusSubject.next(null);
        this.isMonitoring = false;

        // Clear any existing timer
        if (this.currentTimerSubscription) {
            this.currentTimerSubscription.unsubscribe();
            this.currentTimerSubscription = null;
        }
    }

    /**
     * Stop session monitoring (called automatically on destroy)
     */
    ngOnDestroy(): void {
        // Clear timer subscription
        if (this.currentTimerSubscription) {
            this.currentTimerSubscription.unsubscribe();
        }
        // UntilDestroy decorator handles cleanup
    }
}
